import { StoreScheduleResponse } from '@ydistri/api-sdk';
import {
  ActionsBar,
  Button,
  DataTable,
  IconButton_IconSizes,
  LabeledToggle,
  LabelPlacement,
  SearchBox,
  Section,
  Text,
  useInjectLocalFilters,
} from '@ydistri/ds';
import React, { useCallback, useMemo, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import DeleteIconButton from '../../../components/buttons/DeleteIconButton';
import EditIconButton from '../../../components/buttons/EditIconButton';
import { useApplicationConfiguration } from '../../../hooks/useApplicationConfiguration';
import { useDisplayStoreComparator } from '../../../hooks/useDisplayStoreLabel';
import { useStoreTableColumnsProvider } from '../../../hooks/useEntityTableColumnsProvider';
import { useAppDispatch } from '../../../store';
import { AvailableModals, openModal } from '../../Modals/modalSlice';
import ShowArchivedItemsToggle from '../common/ShowArchivedItemsToggle';
import {
  getArchivedItemsCount,
  getArchivedStateSorter,
  onlyActive,
} from '../common/administrationItemsLib';
import { useGetStoreSchedulesQuery } from './apiStoreSchedules';
import StoresScheduleLegend from './StoresScheduleLegend';
import { getStoreScheduleColumns } from './storesScheduleColumns';
import {
  getStoreScheduleRowClassName,
  matchesSearch,
  onlyInEffect,
  storeThenNewestSorter,
  VOCABULARY_NOTICE,
} from './storesScheduleLib';

const createIcon = <MdAdd size={IconButton_IconSizes.small} />;

const StoresScheduleSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState<string>('');
  const [showArchivedItems, setShowArchivedItems] = useState<boolean>(false);
  const [onlyInEffectToday, setOnlyInEffectToday] = useState<boolean>(false);

  const { data: storeSchedules, isFetching, isError } = useGetStoreSchedulesQuery();
  const storeColumnsProvider = useStoreTableColumnsProvider();
  const compareStores = useDisplayStoreComparator();
  const { mdbWarehouseIdAutoIncrement } = useApplicationConfiguration();
  const matchSystemId = mdbWarehouseIdAutoIncrement === 'INT';

  const toggleShowArchived = useCallback(() => {
    setShowArchivedItems(current => !current);
  }, []);

  const toggleOnlyInEffect = useCallback(() => {
    setOnlyInEffectToday(current => !current);
  }, []);

  const searched = useMemo(
    () => (storeSchedules ?? []).filter(record => matchesSearch(record, search, matchSystemId)),
    [storeSchedules, search, matchSystemId],
  );

  const displayedData = useMemo(() => {
    const sorter = storeThenNewestSorter(compareStores);
    // A record in effect has not lapsed, so this subsumes the archived choice rather than
    // combining with it.
    if (onlyInEffectToday) return onlyInEffect(searched).sort(sorter);
    if (showArchivedItems) return Array.from(searched).sort(getArchivedStateSorter(sorter));
    return onlyActive(searched).sort(sorter);
  }, [searched, showArchivedItems, onlyInEffectToday, compareStores]);

  const archivedItemsCount = useMemo(() => getArchivedItemsCount(searched), [searched]);
  const inEffectCount = useMemo(() => onlyInEffect(searched).length, [searched]);

  const openEditModal = useCallback(
    (storeSchedule?: StoreScheduleResponse) => {
      dispatch(openModal({ type: AvailableModals.STORE_SCHEDULE_EDIT, data: { storeSchedule } }));
    },
    [dispatch],
  );

  const openDeleteModal = useCallback(
    (storeSchedule: StoreScheduleResponse) => {
      dispatch(openModal({ type: AvailableModals.STORE_SCHEDULE_DELETE, data: { storeSchedule } }));
    },
    [dispatch],
  );

  const renderActions = useCallback(
    (record: StoreScheduleResponse) =>
      record.isActive ? (
        <ActionsBar>
          <EditIconButton
            size="small"
            tooltip="Edit closure"
            onClick={() => openEditModal(record)}
          />
          <DeleteIconButton
            size="small"
            tooltip="Delete closure"
            onClick={() => openDeleteModal(record)}
          />
        </ActionsBar>
      ) : null,
    [openEditModal, openDeleteModal],
  );

  const columns = useMemo(
    () =>
      getStoreScheduleColumns({
        allRecords: storeSchedules ?? [],
        renderActions,
        storeColumnsProvider,
      }),
    [storeSchedules, renderActions, storeColumnsProvider],
  );

  const filterableColumns = useInjectLocalFilters(columns);

  const headerActions = useMemo(
    () => (
      <ActionsBar>
        <SearchBox
          value={search}
          onSearch={setSearch}
          placeholder="Search store or note"
          id="stores-schedule-search-box"
        />
        <LabeledToggle
          id="StoresScheduleInEffectToggle"
          label={`Show in effect today (${inEffectCount})`}
          checked={onlyInEffectToday}
          onClick={toggleOnlyInEffect}
          labelPlacement={LabelPlacement.LEFT}
          margin="0"
        />
        <ShowArchivedItemsToggle
          archivedItemsCount={archivedItemsCount}
          checked={showArchivedItems}
          onClick={toggleShowArchived}
          disabled={onlyInEffectToday}
          tooltip={
            onlyInEffectToday ? 'An archived closure can never be in effect today.' : undefined
          }
        />
        <Button
          $textVariant="semibold"
          icon={createIcon}
          type="primary"
          onClick={() => openEditModal()}
          data-testid="create_new_store_schedule"
        >
          Create closure
        </Button>
      </ActionsBar>
    ),
    [
      search,
      archivedItemsCount,
      showArchivedItems,
      toggleShowArchived,
      inEffectCount,
      onlyInEffectToday,
      toggleOnlyInEffect,
      openEditModal,
    ],
  );

  return (
    <Section
      header="Stores schedule"
      headerActions={headerActions}
      data-testid="StoresScheduleSection"
    >
      <Text $type="light">{VOCABULARY_NOTICE}</Text>
      <StoresScheduleLegend />
      {isError ? (
        // An empty table would otherwise read as "nothing is scheduled".
        <Text $type="danger">The stores schedule could not be loaded.</Text>
      ) : (
        <DataTable<StoreScheduleResponse>
          showBorder={true}
          showHeader={true}
          id="stores-schedule"
          loading={isFetching}
          columns={filterableColumns}
          dataSource={displayedData}
          size="small"
          rowKey="storeScheduleId"
          rowClassName={getStoreScheduleRowClassName}
        />
      )}
    </Section>
  );
};

export default StoresScheduleSection;
